var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { z } from 'zod';
import { Timeouts } from './framework/Timeouts';
const automateVxResponseAutoSwitchStatus = z.object({
    status: z.literal('OK'),
    results: z.boolean(),
    message: z.string(),
});
const automateVxResponseError = z.object({
    status: z.literal('Error'),
    err: z.string(),
});
const automateVxResponseGetCameras = z.object({
    status: z.literal('OK'),
    message: z.string(),
    cameras: z.array(z.object({
        id: z.number(),
        name: z.string().optional(),
        model: z.string().optional(),
        ip: z.string(),
    })),
});
const automateVxResponseGetLayouts = z.object({
    status: z.literal('OK'),
    message: z.string(),
    layouts: z.array(z.object({
        id: z.string(),
        name: z.string(),
    })),
});
const automateVxResponseGetToken = z.object({
    status: z.literal('OK'),
    token: z.string(),
});
const automateVxResponseLayoutStatus = z.object({
    status: z.literal('OK'),
    message: z.string(),
    layout: z.object({ id: z.string(), name: z.string() }),
});
const automateVxResponseLayoutChange = z.object({
    status: z.literal('OK'),
    message: z.string(),
});
const automateVxResponseManualSwitchStatus = z.object({
    status: z.literal('OK'),
    message: z.string(),
});
const automateVxResponseSuccess = z.object({
    status: z.literal('OK'),
    message: z.string(),
});
const cameraLayout = z.object({
    id: z.string(),
    name: z.string(),
});
function isAutomateVxCameraPreset(config) {
    return 'type' in config && config.type === 'automateVx';
}
function isResp(schema, resp) {
    return schema.safeParse(resp).success;
}
export class AutomateVx {
    constructor(http, state, log, framework) {
        this.http = http;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.apiToken = '';
        this.cameraInfo = {};
        this.deviceInfo = {
            inputs: [],
            outputs: [],
        };
        this.directions = {
            in: '0',
            left: '6',
            out: '1',
            right: '2',
            up: '0',
            down: '4',
        };
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.cameras = {
            type: 'readOnlyCollectionControl',
            get: (config, tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                const cameras = {};
                for (const camera of Object.values((_a = this.cameraInfo) !== null && _a !== void 0 ? _a : {})) {
                    const cameraId = camera.id;
                    // While multiple cameras may be discovered, only the "Auto Switching Camera"
                    // supports camera tracking/auto switching. The detected cameras only support PTZ
                    // controls.
                    cameras[cameraId] = {
                        hasCameraTracking: camera.id === '0',
                        name: camera.name,
                        hasPan: camera.id !== '0',
                        hasTilt: camera.id !== '0',
                        hasZoom: camera.id !== '0',
                        location: 'near',
                    };
                }
                return cameras;
            }),
            getKey: (config) => {
                if (!('id' in config)) {
                    this.log.error(`invalid camera config: ${JSON.stringify(config)}`);
                    return 'invalid';
                }
                return config.id.toString();
            },
        };
        this.cameraMove = {
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                var _b;
                const cameraInfo = this.cameraInfo[key];
                if (cameraInfo === undefined) {
                    this.log.error(`cameraMove: unknown key ${JSON.stringify(key)}`);
                    return value;
                }
                this.state.cameraPreset.update(undefined);
                if ('direction' in value)
                    cameraInfo.moveDirection = value.direction;
                if (value.action === 'start') {
                    cameraInfo.moveSpeed = 'slow';
                    yield this.sendCameraMove('start', cameraInfo, tags);
                }
                else if (value.action === 'stop') {
                    yield this.sendCameraMove('stop', cameraInfo, tags);
                }
                else if (value.action === 'repeat') {
                    if (((_b = cameraInfo.moveSpeed) !== null && _b !== void 0 ? _b : 'slow') === 'slow') {
                        // TODO: can we just start again instead of having to stop first
                        yield this.sendCameraMove('stop', cameraInfo, tags);
                        cameraInfo.moveSpeed = 'fast';
                        yield this.sendCameraMove('start', cameraInfo, tags);
                    }
                    else {
                        yield this.sendCameraMove('continue', cameraInfo, tags);
                    }
                }
                return value;
            }),
        };
        this.cameraPreset = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.cameraPreset.value === key;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const [cameraId, presetNumber] = key.split('-');
                const resp = yield this.httpPostJson({ cmd: 'CallCameraPreset', data: { cam: cameraId, pre: presetNumber } }, automateVxResponseSuccess.or(automateVxResponseError), tags);
                return resp.status === 'OK';
            }),
            getKey: (config) => {
                if (!isAutomateVxCameraPreset(config)) {
                    this.log.error(`invalid camera preset config: ${JSON.stringify(config)}`);
                    return '1-1';
                }
                return `${config.cameraId}-${config.presetIndex}`;
            },
            validate: (config) => __awaiter(this, void 0, void 0, function* () {
                if (!isAutomateVxCameraPreset(config)) {
                    this.log.error(`invalid camera preset config: ${JSON.stringify(config)}`);
                    return { cameraId: 'missing', presetIndex: 'missing' };
                }
                const presetNumber = parseInt(config.presetIndex, 10);
                return {
                    cameraId: Object.values(this.cameraInfo).find(info => parseInt(info.id, 10) === config.cameraId) === undefined
                        ? 'missing'
                        : 'ok',
                    presetIndex: Number.isNaN(presetNumber)
                        ? 'missing'
                        : presetNumber < 1 || presetNumber > 255
                            ? 'range'
                            : 'ok',
                };
            }),
        };
        this.cameraSelected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _c;
                if (this.state.cameraSelected.value !== undefined) {
                    return this.state.cameraSelected.value;
                }
                return Object.keys((_c = this.state.cameras.value) !== null && _c !== void 0 ? _c : {}).sort()[0];
            }),
            set: (value, tags) => __awaiter(this, void 0, void 0, function* () {
                if (value !== undefined) {
                    if (value !== '0') {
                        // If not selecting the autoswitch camera, then manually select the camera to
                        // the output and turn off auto switching.
                        yield this.SelectManualCamera(value);
                    }
                    else {
                        // Turn on auto switching automatically.
                        const resp = yield this.httpPostJson({ cmd: 'StartAutoSwitch' }, automateVxResponseSuccess.or(automateVxResponseError), tags);
                        return resp.status === 'OK' ? value : this.state.cameraSelected.value;
                    }
                }
                return value;
            }),
        };
        this.cameraTracking = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const cameraInfo = this.cameraInfo[key];
                if (cameraInfo === undefined) {
                    this.log.error(`cameraTracking: unknown key ${JSON.stringify(key)}`);
                    return this.state.cameraTracking.values[key];
                }
                const resp = yield this.httpPostJson({ cmd: 'AutoSwitchStatus' }, automateVxResponseAutoSwitchStatus, tags);
                return resp.results;
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const cameraInfo = this.cameraInfo[key];
                if (cameraInfo === undefined) {
                    this.log.error(`cameraTracking: unknown key ${JSON.stringify(key)}`);
                    return value;
                }
                const cmd = value ? 'StartAutoSwitch' : 'StopAutoSwitch';
                const resp = yield this.httpPostJson({ cmd }, automateVxResponseSuccess.or(automateVxResponseError), tags);
                return resp.status === 'OK' ? value : this.state.cameraTracking.values[key];
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return {
                    videoInputs: this.deviceInfo.inputs,
                    videoOutputs: this.deviceInfo.outputs,
                };
            }),
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.power.value;
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                this.state.busy.update(true);
                yield this.httpPostJson({ cmd: power ? 'Wake' : 'Sleep' }, automateVxResponseSuccess, tags);
                this.busyTimeout.set(() => this.state.busy.update(false), 2000);
                return power;
            }),
        };
        this.videoMatrixRoutes = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const resp = yield this.httpPostJson({ cmd: 'LayoutStatus' }, automateVxResponseLayoutStatus.or(automateVxResponseError), tags);
                return resp.status === 'OK'
                    ? `${key}.${resp.layout.id}`
                    : this.state.videoMatrixRoutes.values[key];
            }),
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                const [, inputId] = value.split('.');
                const resp = yield this.httpPostJson({ cmd: 'ChangeLayout', data: { id: inputId } }, automateVxResponseLayoutChange.or(automateVxResponseError), tags);
                return resp.status === 'OK' ? value : this.state.videoMatrixRoutes.values[key];
            }),
        };
        // polling
        this.polls = [
            { intervalMs: 5000, controls: ['videoMatrixRoutes', 'cameraTracking'] },
            {
                intervalMs: 1 * 60 * 1000,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    yield this.queryLayouts(true);
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            this.cameraInfo = {};
            this.timeouts.clearAll();
            // detect cameras
            const resp = yield this.httpPostJson({ cmd: 'GetCameras' }, automateVxResponseGetCameras, [
                'get.cameras',
            ]);
            const cameras = resp.cameras;
            for (const camera of cameras) {
                const driverKey = camera.id.toString();
                const name = camera.name ? camera.name : ((_a = camera.model) !== null && _a !== void 0 ? _a : 'Camera') + ` ${driverKey}`;
                this.cameraInfo[driverKey] = {
                    hasPower: true,
                    id: driverKey,
                    invertPan: config.invertPan,
                    name,
                };
            }
            // Add the overall Automate System camera as camera 0.
            const driverKey = '0';
            this.cameraInfo[driverKey] = {
                hasPower: true,
                id: driverKey,
                invertPan: false,
                name: (_b = config.cameraName) !== null && _b !== void 0 ? _b : 'Auto Camera Switcher',
            };
            yield this.queryLayouts(false);
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    onInitialize(credentials, config) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const username = (_a = credentials.username) !== null && _a !== void 0 ? _a : 'admin';
            const password = (_b = credentials.password) !== null && _b !== void 0 ? _b : '1beyond';
            const authToken = btoa(`${username}:${password}`);
            try {
                const resp = yield this.http.requestJson('post', `:3579/Get-Token`, {
                    headers: { Authorization: authToken },
                });
                if (!isResp(automateVxResponseGetToken, resp))
                    return 'authFailed';
                this.apiToken = resp.token;
                return 'connected';
            }
            catch (error) {
                return 'verifyFailed';
            }
            return 'verifyFailed';
        });
    }
    // other class methods
    httpPostJson(cmd, schema, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestOptions = {};
            requestOptions.headers = { Authorization: this.apiToken };
            if ('data' in cmd)
                requestOptions.data = cmd.data;
            requestOptions.tags = tags;
            const resp = yield this.http.requestJson('post', `:3579/api/${cmd.cmd}`, requestOptions);
            if (!isResp(schema, resp)) {
                throw new Error(`unexpected response: ${JSON.stringify(resp)}`);
            }
            return resp;
        });
    }
    queryLayouts(isPoll) {
        return __awaiter(this, void 0, void 0, function* () {
            const tags = isPoll ? ['get.detected', 'poll'] : ['get.detected'];
            const layoutResp = yield this.httpPostJson({ cmd: 'GetLayouts' }, automateVxResponseGetLayouts, tags);
            const layouts = layoutResp.layouts;
            this.deviceInfo.inputs.length = 0;
            this.deviceInfo.outputs.length = 0;
            for (const layout of layouts) {
                const key = `layout.${layout.id}`;
                this.deviceInfo.inputs.push({ code: key, external: false, name: layout.name });
            }
            this.deviceInfo.outputs.push({ code: 'layout', name: 'Camera Layouts' });
            this.state.detected.update({
                videoInputs: this.deviceInfo.inputs,
                videoOutputs: this.deviceInfo.outputs,
            });
        });
    }
    sendCameraMove(action, cameraInfo, tags) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            let cmd;
            const camId = cameraInfo.id;
            const direction = cameraInfo.moveDirection === 'left'
                ? cameraInfo.invertPan
                    ? 'right'
                    : 'left'
                : cameraInfo.moveDirection === 'right'
                    ? cameraInfo.invertPan
                        ? 'left'
                        : 'right'
                    : (_a = cameraInfo.moveDirection) !== null && _a !== void 0 ? _a : 'left';
            if (action === 'start') {
                if (['left', 'right', 'up', 'down'].includes(direction !== null && direction !== void 0 ? direction : 'none')) {
                    cmd = {
                        cmd: 'StartPT',
                        data: { cam: camId, ptDir: this.directions[direction] },
                    };
                }
                else if (direction === 'out' || direction === 'in') {
                    cmd = {
                        cmd: 'StartZ',
                        data: { cam: camId, zDir: this.directions[direction] },
                    };
                }
                else {
                    this.log.error(`invalid move direction: ${cameraInfo.moveDirection}`);
                    return;
                }
            }
            else if (action === 'stop') {
                if (['left', 'right', 'up', 'down'].includes(direction !== null && direction !== void 0 ? direction : 'none')) {
                    cmd = { cmd: `StopPT`, data: { cam: camId } };
                }
                else if (['in', 'out'].includes(direction !== null && direction !== void 0 ? direction : 'none')) {
                    cmd = { cmd: `StopZ`, data: { cam: camId } };
                }
                else {
                    this.log.error(`invalid move direction: ${cameraInfo.moveDirection}`);
                    return;
                }
            }
            else if (action === 'continue') {
                // nop
            }
            if (cameraInfo.moveTimeout) {
                cameraInfo.moveTimeout.clear();
                cameraInfo.moveTimeout = undefined;
            }
            if (cmd !== undefined) {
                yield this.httpPostJson(cmd, automateVxResponseSuccess, tags);
            }
            if (action === 'start' || action === 'continue') {
                cameraInfo.moveTimeout = this.timeouts.set(() => __awaiter(this, void 0, void 0, function* () {
                    cameraInfo.moveTimeout = undefined;
                    this.log.error(`camera move timeout for camera id ${cameraInfo.id}`);
                    yield this.sendCameraMove('stop', cameraInfo, tags);
                }), 1000);
            }
        });
    }
    SelectManualCamera(id) {
        return __awaiter(this, void 0, void 0, function* () {
            // Turn off camera auto switching of the system if manually selecting a camera.
            if (this.state.cameraTracking.values['0']) {
                this.cameraTracking.set(id, false, ['set.cameraTracking']);
            }
            // Select the desired camera.
            yield this.httpPostJson({ cmd: 'ManualSwitchCamera', data: { address: id } }, automateVxResponseManualSwitchStatus.or(automateVxResponseError), ['set.manualSwitchCamera']);
        });
    }
}
// static properties
AutomateVx.driverType = 'httpDriver';
AutomateVx.key = '-NQV8kNRGYQ12DTC9d0p';
AutomateVx.httpRequestOptions = { scheme: 'http' };
